import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

export const WaitlistDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", focus: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await joinWaitlist({
        name: form.name.trim(),
        email: form.email.trim(),
        focus: form.focus.trim() || null,
      });
      toast.success("You have joined the Outer Circle. Watch for the moon.");
      setForm({ name: "", email: "", focus: "" });
      onClose();
    } catch (err) {
      toast.error("Something went awry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="waitlist-dialog"
        >
          <div
            className="absolute inset-0 bg-[#050505]/85 backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="glass relative w-full max-w-md rounded-2xl p-8 shadow-[0_0_60px_rgba(59,42,72,0.7)]"
          >
            <button
              onClick={onClose}
              className="absolute right-5 top-5 text-white/40 transition-colors hover:text-[#C5A059]"
              data-testid="waitlist-close-btn"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            <p className="overline mb-4">Join the Outer Circle</p>
            <h3 className="font-serif text-3xl font-light tracking-tight text-white">
              The moon will call you.
            </h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-white/50">
              Receive first word of new relics, mysteries, and open seats at the
              Council.
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="w-full rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#C5A059]/70"
                data-testid="waitlist-name-input"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Your email"
                className="w-full rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#C5A059]/70"
                data-testid="waitlist-email-input"
              />
              <input
                value={form.focus}
                onChange={(e) => setForm({ ...form, focus: e.target.value })}
                placeholder="Your field of power (optional)"
                className="w-full rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#C5A059]/70"
                data-testid="waitlist-focus-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-[#C5A059] py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                data-testid="waitlist-submit-btn"
              >
                {loading ? "Sealing…" : "Enter the Outer Circle"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WaitlistDialog;
